/**
 * VitalMind — Ultimate Anti-Gravity UX Layer v3
 * ─────────────────────────────────────────────────
 * DESKTOP (pointer: fine)
 *   • Magnetic custom cursor — glowing dot + lagged spring ring
 *   • Velocity cursor trail  — 10 ghost dots with opacity/size decay
 *   • Constellation canvas  — 60 GPU-accelerated particles w/ mouse repel
 *   • Ambient mouse glow    — radial emerald light follows cursor
 *   • 3-D card tilt          — CSS custom properties (--tilt-x/y, --shine-x/y)
 *
 * ALL DEVICES
 *   • Scroll progress bar   — thin emerald line at top of viewport
 *   • Touch ripple          — tap feedback on mobile (touch devices only)
 */
import { useEffect, useRef, useCallback } from "react";

const TRAIL_LEN = 10;

/* ── Particle config ──────────────────────────────────────────────────────── */
const PARTICLE_COUNT = 60;
const BASE_SPEED     = 0.28;
const LINK_DIST      = 130;
const MOUSE_REPEL    = 110;
const COLORS         = ["16,185,129", "6,182,212", "139,92,246", "59,130,246"];

export default function UXLayer() {
  /* ── Refs ──────────────────────────────────────────────────────────────── */
  const canvasRef   = useRef(null);
  const glowRef     = useRef(null);
  const dotRef      = useRef(null);
  const ringWrapRef = useRef(null);
  const ringVisRef  = useRef(null);
  const progressRef = useRef(null);
  const mouse       = useRef({ x: -999, y: -999 });
  const ringPos     = useRef({ x: -999, y: -999 });
  const particles   = useRef([]);
  const hovering    = useRef(false);
  const raf         = useRef(null);

  /* trail */
  const trailPos  = useRef(Array.from({ length: TRAIL_LEN }, () => ({ x: -999, y: -999 })));
  const trailRefs = useRef([]);

  const initParticles = useCallback((w, h) => {
    particles.current = Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
      x:  Math.random() * w,
      y:  Math.random() * h,
      vx: (Math.random() - 0.5) * BASE_SPEED * 2,
      vy: (Math.random() - 0.5) * BASE_SPEED * 2,
      r:  Math.random() * 1.6 + 0.5,
      color: COLORS[i % COLORS.length],
      op:    Math.random() * 0.38 + 0.1,
    }));
  }, []);

  /* ── Scroll progress bar (all devices) ───────────────────────────────── */
  useEffect(() => {
    const bar = progressRef.current;
    if (!bar) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const pct = scrollHeight <= clientHeight
        ? 0
        : (scrollTop / (scrollHeight - clientHeight)) * 100;
      bar.style.width = `${pct}%`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* ── Touch ripple (mobile only) ─────────────────────────────────────── */
  useEffect(() => {
    if (!window.matchMedia("(pointer: coarse)").matches) return;

    const createRipple = (e) => {
      const touch = e.touches[0];
      if (!touch) return;

      const ripple = document.createElement("span");
      ripple.style.cssText = `
        position: fixed;
        left: ${touch.clientX - 24}px;
        top:  ${touch.clientY - 24}px;
        width: 48px; height: 48px;
        border-radius: 50%;
        background: rgba(16,185,129,0.25);
        pointer-events: none;
        z-index: 9990;
        transform: scale(0);
        animation: touchRipple 0.55s cubic-bezier(0.4,0,0.2,1) forwards;
      `;
      document.body.appendChild(ripple);
      setTimeout(() => ripple.remove(), 600);
    };

    window.addEventListener("touchstart", createRipple, { passive: true });
    return () => window.removeEventListener("touchstart", createRipple);
  }, []);

  /* ── Desktop: cursor + particles + tilt ─────────────────────────────── */
  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;

    const canvas   = canvasRef.current;
    const ctx      = canvas.getContext("2d");
    const glow     = glowRef.current;
    const dot      = dotRef.current;
    const ringWrap = ringWrapRef.current;
    const ringVis  = ringVisRef.current;

    /* resize */
    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      initParticles(canvas.width, canvas.height);
    };
    resize();
    window.addEventListener("resize", resize);

    document.body.style.cursor = "none";

    /* mouse move */
    const onMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
      if (glow) {
        glow.style.background = `radial-gradient(700px circle at ${e.clientX}px ${e.clientY}px, rgba(16,185,129,0.055), transparent 65%)`;
      }
      if (dot) {
        dot.style.transform = `translate3d(${e.clientX - 3}px,${e.clientY - 3}px,0)`;
      }
    };
    window.addEventListener("mousemove", onMove);

    /* hover detection */
    const SELECTORS = "a,button,[role='button'],input,textarea,select,label,.hover-cursor";
    const onEnter = () => {
      hovering.current = true;
      ringVis?.classList.add("cursor-ring--active");
      dot?.classList.add("cursor-dot--active");
    };
    const onLeave = () => {
      hovering.current = false;
      ringVis?.classList.remove("cursor-ring--active");
      dot?.classList.remove("cursor-dot--active");
    };
    const bindHover = () => {
      document.querySelectorAll(SELECTORS).forEach((el) => {
        if (el._uxBound) return;
        el._uxBound = true;
        el.style.cursor = "none";
        el.addEventListener("mouseenter", onEnter);
        el.addEventListener("mouseleave", onLeave);
      });
    };
    bindHover();
    const hoverObs = new MutationObserver(bindHover);
    hoverObs.observe(document.body, { childList: true, subtree: true });

    /* 3-D card tilt */
    const TILT_SEL = ".glass-card,.tilt-card,.stat-card,.service-card,.glass-panel,.depth-card";
    const MAX_TILT = 14;
    const onTiltMove = (e) => {
      const el = e.currentTarget;
      const r  = el.getBoundingClientRect();
      const nx = (e.clientX - (r.left + r.width / 2))  / (r.width  / 2);
      const ny = (e.clientY - (r.top  + r.height / 2)) / (r.height / 2);
      el.style.setProperty("--tilt-x", `${(-ny * MAX_TILT).toFixed(2)}deg`);
      el.style.setProperty("--tilt-y", `${( nx * MAX_TILT).toFixed(2)}deg`);
      el.style.setProperty("--shine-x", `${((nx + 1) / 2 * 100).toFixed(1)}%`);
      el.style.setProperty("--shine-y", `${((ny + 1) / 2 * 100).toFixed(1)}%`);
    };
    const onTiltLeave = (e) => {
      e.currentTarget.style.setProperty("--tilt-x", "0deg");
      e.currentTarget.style.setProperty("--tilt-y", "0deg");
    };
    const bindTilt = () => {
      document.querySelectorAll(TILT_SEL).forEach((el) => {
        if (el._tiltBound) return;
        el._tiltBound = true;
        el.addEventListener("mousemove",  onTiltMove);
        el.addEventListener("mouseleave", onTiltLeave);
      });
    };
    setTimeout(bindTilt, 600);
    const tiltObs = new MutationObserver(bindTilt);
    tiltObs.observe(document.body, { childList: true, subtree: true });

    /* animation loop */
    const animate = () => {
      raf.current = requestAnimationFrame(animate);

      const w  = canvas.width;
      const h  = canvas.height;
      const mx = mouse.current.x;
      const my = mouse.current.y;

      /* spring ring */
      ringPos.current.x += (mx - ringPos.current.x) * 0.09;
      ringPos.current.y += (my - ringPos.current.y) * 0.09;
      if (ringWrap) {
        ringWrap.style.transform = `translate3d(${ringPos.current.x}px,${ringPos.current.y}px,0)`;
      }

      /* velocity trail — shift buffer and move DOM nodes */
      const trail = trailPos.current;
      for (let t = TRAIL_LEN - 1; t > 0; t--) {
        trail[t].x = trail[t - 1].x;
        trail[t].y = trail[t - 1].y;
      }
      trail[0].x += (mx - trail[0].x) * 0.38;
      trail[0].y += (my - trail[0].y) * 0.38;
      const trailEls = trailRefs.current;
      for (let t = 0; t < TRAIL_LEN; t++) {
        const el = trailEls[t];
        if (!el) continue;
        const frac    = 1 - t / TRAIL_LEN;
        const size    = frac * 4 + 1;           // 5px → 1px
        const opacity = frac * 0.45;
        el.style.transform = `translate3d(${trail[t].x - size / 2}px,${trail[t].y - size / 2}px,0)`;
        el.style.width      = `${size}px`;
        el.style.height     = `${size}px`;
        el.style.opacity    = opacity;
      }

      /* particles */
      ctx.clearRect(0, 0, w, h);
      const pts = particles.current;

      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        const dx   = p.x - mx;
        const dy   = p.y - my;
        const dist = Math.hypot(dx, dy);
        if (dist < MOUSE_REPEL && dist > 0) {
          const f = ((MOUSE_REPEL - dist) / MOUSE_REPEL) * 0.35;
          p.vx += (dx / dist) * f;
          p.vy += (dy / dist) * f;
        }
        p.vx *= 0.975; p.vy *= 0.975;
        p.x  += p.vx;  p.y  += p.vy;
        if (p.x < 0) p.x = w; else if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; else if (p.y > h) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color},${p.op})`;
        ctx.fill();

        for (let j = i + 1; j < pts.length; j++) {
          const q = pts[j];
          const d = Math.hypot(p.x - q.x, p.y - q.y);
          if (d < LINK_DIST) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y); ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(${p.color},${(1 - d / LINK_DIST) * 0.13})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
    };
    raf.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      if (raf.current) cancelAnimationFrame(raf.current);
      hoverObs.disconnect();
      tiltObs.disconnect();
      document.body.style.cursor = "";
    };
  }, [initParticles]);

  return (
    <>
      {/* ── Scroll progress bar (always visible) ─────────────────────── */}
      <div
        ref={progressRef}
        className="scroll-progress-bar"
        aria-hidden="true"
      />

      {/* ── Ambient mouse glow (desktop) ─────────────────────────────── */}
      <div
        ref={glowRef}
        className="pointer-events-none fixed inset-0 z-[2] transition-[background] duration-500"
        aria-hidden="true"
      />

      {/* ── Constellation canvas (desktop) ────────────────────────────── */}
      <canvas
        ref={canvasRef}
        className="pointer-events-none fixed inset-0 z-[2]"
        aria-hidden="true"
      />

      {/* ── Cursor dot (desktop) ─────────────────────────────────────── */}
      <div
        ref={dotRef}
        className="cursor-dot pointer-events-none fixed top-0 left-0 z-[9999]"
        aria-hidden="true"
      />

      {/* ── Cursor ring wrapper + inner (desktop) ─────────────────────── */}
      <div
        ref={ringWrapRef}
        className="pointer-events-none fixed top-0 left-0 z-[9998]"
        aria-hidden="true"
      >
        <div ref={ringVisRef} className="cursor-ring" />
      </div>

      {/* ── Cursor velocity trail (desktop) ───────────────────────────── */}
      {Array.from({ length: TRAIL_LEN }).map((_, i) => (
        <div
          key={i}
          ref={(el) => { trailRefs.current[i] = el; }}
          className="cursor-trail-dot pointer-events-none fixed top-0 left-0"
          style={{ zIndex: 9997 - i }}
          aria-hidden="true"
        />
      ))}
    </>
  );
}
